import { FC, lazy, Suspense, useCallback, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CircularProgress from "@mui/material/CircularProgress";
import { FormConfig } from "../../../domain/event.interface";
import { Version as surveyVersion } from "survey-core";

const SurveyCreatorWrapper = lazy(() =>
	import("./SurveyCreatorWrapper").then((m) => ({
		default: m.SurveyCreatorWrapper,
	})),
);

interface FormConfigSectionProps {
	formConfig: FormConfig;
	onChange: (config: FormConfig) => void;
	errors?: Record<string, string>;
	userId?: string;
}

function countQuestions(schema: Record<string, unknown>): number {
	if (!schema || !schema.pages) return 0;
	const pages = schema.pages as Array<{ elements?: unknown[] }>;
	return pages.reduce((sum, page) => sum + (page.elements?.length || 0), 0);
}

export const FormConfigSection: FC<FormConfigSectionProps> = ({
	formConfig,
	onChange,
	errors,
	userId,
}) => {
	const [dialogOpen, setDialogOpen] = useState(false);

	const hasQuestions =
		formConfig.surveySchema && Object.keys(formConfig.surveySchema).length > 0;

	const questionCount = countQuestions(formConfig.surveySchema);

	const handleSchemaChange = useCallback(
		(schema: Record<string, unknown>) => {
			onChange({
				...formConfig,
				surveySchema: schema,
				surveySchemaVersion: surveyVersion,
				uploadedAt: new Date().toISOString(),
				uploadedBy: userId,
			});
		},
		[formConfig, onChange, userId],
	);

	return (
		<Grid item xs={12}>
			<Box
				sx={{
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
					p: 2,
					mt: 1,
				}}
			>
				<Typography variant="subtitle1" sx={{ mb: 2 }}>
					Survey Form Configuration
				</Typography>

				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					{hasQuestions
						? `Survey configured with ${questionCount} question(s)`
						: "No survey designed yet. Click below to open the form builder."}
				</Typography>

				{errors?.formConfig && (
					<Typography variant="body2" color="error" sx={{ mb: 1 }}>
						{errors.formConfig}
					</Typography>
				)}

				<Button
					variant="outlined"
					startIcon={<EditIcon />}
					onClick={() => setDialogOpen(true)}
				>
					{hasQuestions ? "Edit Survey" : "Design Survey"}
				</Button>

				<Dialog
					fullScreen
					open={dialogOpen}
					onClose={() => setDialogOpen(false)}
				>
					<AppBar sx={{ position: "relative" }}>
						<Toolbar>
							<IconButton
								edge="start"
								color="inherit"
								onClick={() => setDialogOpen(false)}
							>
								<CloseIcon />
							</IconButton>
							<Typography sx={{ ml: 2, flex: 1 }} variant="h6">
								Survey Form Builder
							</Typography>
							<Button
								autoFocus
								color="inherit"
								onClick={() => setDialogOpen(false)}
							>
								Done
							</Button>
						</Toolbar>
					</AppBar>
					<Suspense
						fallback={
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									mt: 10,
								}}
							>
								<CircularProgress />
							</Box>
						}
					>
						<SurveyCreatorWrapper
							initialSchema={formConfig.surveySchema}
							onSchemaChange={handleSchemaChange}
						/>
					</Suspense>
				</Dialog>
			</Box>
		</Grid>
	);
};
